// Formatting and shared reference data. Anything a page displays as a number,
// a date or a label comes from here so the presentation stays consistent.

export const n0 = (v) => (v === null || v === undefined || Number.isNaN(+v) ? '–' : Math.round(+v).toLocaleString());
export const n1 = (v) => (v === null || v === undefined || Number.isNaN(+v) ? '–' : (+v).toFixed(1));
export const n2 = (v) => (v === null || v === undefined || Number.isNaN(+v) ? '–' : (+v).toFixed(2));

export const today = () => new Date().toISOString().slice(0, 10);

export const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '–';

export const longDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '–';

export const timeOf = (iso) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';

export const titleise = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/* ── Macros ────────────────────────────────────────────────────────────── */

export const MACROS = [
  { key: 'calories', label: 'Calories', unit: 'kcal', color: 'var(--macro-cal)' },
  { key: 'protein',  label: 'Protein',  unit: 'g',    color: 'var(--macro-protein)' },
  { key: 'carbs',    label: 'Carbs',    unit: 'g',    color: 'var(--macro-carbs)' },
  { key: 'fats',     label: 'Fats',     unit: 'g',    color: 'var(--macro-fats)' },
];

export const MEAL_TIMES = ['breakfast', 'lunch', 'dinner', 'snack'];

export const MUSCLE_GROUPS = [
  'chest', 'back', 'shoulders', 'arms', 'legs', 'core', 'cardio', 'full body',
];

/* ── Plans (static reference data shown on the Plans page) ─────────────── */

export const DIET_PLANS = {
  balanced: {
    name: 'Balanced Macros',
    description: '40% carbs · 30% protein · 30% fats',
    calories: 2000, protein: 150, carbs: 200, fats: 67,
    meals: [
      { meal: 'Breakfast', items: 'Oatmeal with berries, eggs, orange juice' },
      { meal: 'Lunch', items: 'Grilled chicken, brown rice, vegetables' },
      { meal: 'Snack', items: 'Greek yogurt, almonds' },
      { meal: 'Dinner', items: 'Salmon, quinoa, broccoli' },
    ],
  },
  'high-protein': {
    name: 'High Protein',
    description: '35% carbs · 40% protein · 25% fats — muscle gain',
    calories: 2400, protein: 240, carbs: 210, fats: 67,
    meals: [
      { meal: 'Breakfast', items: 'Protein smoothie, wholegrain toast, peanut butter' },
      { meal: 'Lunch', items: 'Lean beef, sweet potato, green beans' },
      { meal: 'Snack', items: 'Protein bar, banana' },
      { meal: 'Dinner', items: 'Chicken breast, pasta, spinach' },
    ],
  },
  'low-carb': {
    name: 'Low Carb',
    description: '20% carbs · 40% protein · 40% fats — fat loss',
    calories: 1800, protein: 180, carbs: 90, fats: 80,
    meals: [
      { meal: 'Breakfast', items: 'Scrambled eggs, avocado, bacon' },
      { meal: 'Lunch', items: 'Grilled fish, cauliflower rice' },
      { meal: 'Snack', items: 'Cheese, nuts' },
      { meal: 'Dinner', items: 'Steak, asparagus, olive oil' },
    ],
  },
};

export const WORKOUT_PLANS = {
  'beginner-full-body': {
    name: 'Beginner Full Body',
    description: '3 sessions a week, whole body each time',
    schedule: [
      { day: 'Monday — Full Body A', exercises: [
        { name: 'Squats', sets: 3, reps: 10 }, { name: 'Bench Press', sets: 3, reps: 10 },
        { name: 'Bent Over Rows', sets: 3, reps: 10 }, { name: 'Overhead Press', sets: 3, reps: 8 },
        { name: 'Plank', sets: 3, reps: '30s' }] },
      { day: 'Wednesday — Full Body B', exercises: [
        { name: 'Deadlifts', sets: 3, reps: 8 }, { name: 'Incline DB Press', sets: 3, reps: 10 },
        { name: 'Lat Pulldowns', sets: 3, reps: 10 }, { name: 'Lateral Raises', sets: 3, reps: 12 },
        { name: 'Russian Twists', sets: 3, reps: 20 }] },
      { day: 'Friday — Full Body A', exercises: [
        { name: 'Front Squats', sets: 3, reps: 10 }, { name: 'Dips', sets: 3, reps: 10 },
        { name: 'Seated Row', sets: 3, reps: 12 }, { name: 'Face Pulls', sets: 3, reps: 15 }] },
    ],
  },
  'ppl-split': {
    name: 'Push / Pull / Legs',
    description: '6 sessions a week, intermediate to advanced',
    schedule: [
      { day: 'Push', exercises: [
        { name: 'Bench Press', sets: 4, reps: 8 }, { name: 'Overhead Press', sets: 4, reps: 8 },
        { name: 'Incline DB Press', sets: 3, reps: 10 }, { name: 'Tricep Pushdowns', sets: 3, reps: 12 }] },
      { day: 'Pull', exercises: [
        { name: 'Deadlifts', sets: 4, reps: 6 }, { name: 'Pull-ups', sets: 3, reps: 10 },
        { name: 'Barbell Rows', sets: 3, reps: 10 }, { name: 'Barbell Curls', sets: 3, reps: 10 }] },
      { day: 'Legs', exercises: [
        { name: 'Squats', sets: 4, reps: 8 }, { name: 'Romanian Deadlifts', sets: 3, reps: 10 },
        { name: 'Leg Press', sets: 3, reps: 12 }, { name: 'Calf Raises', sets: 4, reps: 15 }] },
    ],
  },
  'upper-lower': {
    name: 'Upper / Lower Split',
    description: '4 sessions a week, balanced',
    schedule: [
      { day: 'Upper A', exercises: [
        { name: 'Bench Press', sets: 4, reps: 8 }, { name: 'Bent Over Rows', sets: 4, reps: 8 },
        { name: 'Overhead Press', sets: 3, reps: 10 }, { name: 'Pull-ups', sets: 3, reps: 8 }] },
      { day: 'Lower A', exercises: [
        { name: 'Squats', sets: 4, reps: 8 }, { name: 'Romanian Deadlifts', sets: 3, reps: 10 },
        { name: 'Leg Curls', sets: 3, reps: 12 }, { name: 'Calf Raises', sets: 4, reps: 15 }] },
      { day: 'Upper B', exercises: [
        { name: 'Incline Press', sets: 4, reps: 10 }, { name: 'Cable Rows', sets: 4, reps: 10 },
        { name: 'Lateral Raises', sets: 3, reps: 15 }] },
      { day: 'Lower B', exercises: [
        { name: 'Front Squats', sets: 4, reps: 10 }, { name: 'Hip Thrusts', sets: 3, reps: 12 },
        { name: 'Leg Extensions', sets: 3, reps: 15 }] },
    ],
  },
};

/* ── Bloodwork panels ──────────────────────────────────────────────────── */
// Grouping, units and ranges shown beside each input. The backend is the
// authority on analysis; these are display hints only.

export const BLOOD_PANELS = [
  {
    id: 'cbc', name: 'Complete Blood Count', accent: 'var(--crit)',
    markers: [
      { key: 'hemoglobin', label: 'Haemoglobin', unit: 'g/dL', ref: '13.2–16.6' },
      { key: 'hematocrit', label: 'Haematocrit', unit: '%', ref: '38.3–48.6' },
      { key: 'wbc', label: 'White cells', unit: '10⁹/L', ref: '3.4–9.6' },
      { key: 'rbc', label: 'Red cells', unit: '10¹²/L', ref: '4.35–5.65' },
      { key: 'platelets', label: 'Platelets', unit: '10⁹/L', ref: '135–317' },
    ],
  },
  {
    id: 'lipid', name: 'Lipid Profile', accent: 'var(--warn)',
    markers: [
      { key: 'total_cholesterol', label: 'Total cholesterol', unit: 'mg/dL', ref: '< 200' },
      { key: 'ldl', label: 'LDL', unit: 'mg/dL', ref: '< 100' },
      { key: 'hdl', label: 'HDL', unit: 'mg/dL', ref: '> 40' },
      { key: 'triglycerides', label: 'Triglycerides', unit: 'mg/dL', ref: '< 150' },
    ],
  },
  {
    id: 'metabolic', name: 'Metabolic Profile', accent: 'var(--accent)',
    markers: [
      { key: 'glucose', label: 'Glucose (fasting)', unit: 'mg/dL', ref: '70–99' },
      { key: 'hba1c', label: 'HbA1c', unit: '%', ref: '4.0–5.6' },
      { key: 'creatinine', label: 'Creatinine', unit: 'mg/dL', ref: '0.74–1.35' },
      { key: 'egfr', label: 'eGFR', unit: 'mL/min', ref: '> 90' },
      { key: 'alt', label: 'ALT', unit: 'U/L', ref: '7–55' },
      { key: 'ast', label: 'AST', unit: 'U/L', ref: '8–48' },
      { key: 'uric_acid', label: 'Uric acid', unit: 'mg/dL', ref: '3.4–7.0' },
    ],
  },
  {
    id: 'hormone', name: 'Hormone Profile', accent: 'var(--info)',
    markers: [
      { key: 'testosterone', label: 'Testosterone', unit: 'ng/dL', ref: '250–1000' },
      { key: 'tsh', label: 'TSH', unit: 'mIU/L', ref: '0.3–4.0' },
      { key: 't3', label: 'T3', unit: 'ng/dL', ref: '80–200' },
      { key: 't4', label: 'T4', unit: 'mcg/dL', ref: '4.5–12.0' },
    ],
  },
  {
    id: 'vitamins', name: 'Vitamins & Minerals', accent: 'var(--ok)',
    markers: [
      { key: 'vitamin_d', label: 'Vitamin D', unit: 'ng/mL', ref: '> 30' },
      { key: 'vitamin_b12', label: 'Vitamin B12', unit: 'pg/mL', ref: '200–900' },
      { key: 'iron', label: 'Iron', unit: 'mcg/dL', ref: '60–170' },
      { key: 'ferritin', label: 'Ferritin', unit: 'ng/mL', ref: '24–336' },
    ],
  },
  {
    id: 'inflammation', name: 'Inflammation & Prostate', accent: 'var(--macro-fats)',
    markers: [
      { key: 'crp', label: 'CRP', unit: 'mg/L', ref: '< 3.0' },
      { key: 'homocysteine', label: 'Homocysteine', unit: 'µmol/L', ref: '4.3–11.4' },
      { key: 'psa', label: 'PSA', unit: 'ng/mL', ref: '< 3.0 (male)' },
    ],
  },
];

export const ALL_MARKERS = BLOOD_PANELS.flatMap((p) => p.markers);
export const MARKER_LABEL = Object.fromEntries(ALL_MARKERS.map((m) => [m.key, m.label]));

/* ── Derived body metrics ──────────────────────────────────────────────── */

export function bodyMetrics(profile, latestWeight) {
  const height = profile?.height;
  const weight = profile?.current_weight;
  if (!height || !weight) return { bmi: null, ffmi: null, bodyFat: latestWeight?.body_fat ?? null };

  const m = height / 100;
  const bmi = weight / (m * m);
  const bodyFat = latestWeight?.body_fat ?? null;
  const ffmi = bodyFat ? (weight * (1 - bodyFat / 100)) / (m * m) : null;
  return { bmi, ffmi, bodyFat };
}

export const bmiBand = (bmi) => {
  if (bmi === null) return { label: '–', tone: 'muted' };
  if (bmi < 18.5) return { label: 'Underweight', tone: 'warn' };
  if (bmi < 25) return { label: 'Healthy', tone: 'ok' };
  if (bmi < 30) return { label: 'Overweight', tone: 'warn' };
  return { label: 'Obese', tone: 'crit' };
};

export const riskBand = (score) => {
  if (score > 60) return { label: 'High', tone: 'crit', color: 'var(--crit)' };
  if (score > 40) return { label: 'Moderate', tone: 'warn', color: 'var(--warn)' };
  return { label: 'Low', tone: 'ok', color: 'var(--ok)' };
};
